// Service for managing appointments using Firebase Firestore
import {
  addAppointment as firestoreAddAppointment,
  getAppointments as firestoreGetAppointments,
  updateAppointment as firestoreUpdateAppointment,
  deleteAppointment as firestoreDeleteAppointment,
  Appointment
} from '../firebase/firestore';
import { onSnapshot, collection, query, orderBy, where, Timestamp } from 'firebase/firestore';
import { db } from '../firebase/firebase-config';

// Re-export the Appointment interface from firestore
export type { Appointment };

// Subscribe to real-time appointment updates
export const subscribeToAppointments = (callback: (appointments: Appointment[]) => void): (() => void) => {
  const q = query(collection(db, "appointments"), orderBy("date", "asc"));
  
  return onSnapshot(q, (snapshot) => {
    const appointments = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    } as Appointment));
    callback(appointments);
  });
};

// Subscribe to today's appointments
export const subscribeToTodaysAppointments = (callback: (appointments: Appointment[]) => void): (() => void) => {
  const today = new Date();
  const todayStr = today.toISOString().split('T')[0]; // YYYY-MM-DD format
  
  const q = query(
    collection(db, "appointments"), 
    where("date", "==", todayStr),
    orderBy("time", "asc")
  );
  
  return onSnapshot(q, (snapshot) => {
    const appointments = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    } as Appointment));
    callback(appointments);
  });
};

// Add new appointment
export const addAppointment = async (appointment: Omit<Appointment, 'id' | '_id' | 'createdAt' | 'updatedAt'>): Promise<string> => {
  return await firestoreAddAppointment(appointment);
};

// Update appointment
export const updateAppointment = async (id: string, updates: Partial<Appointment>): Promise<void> => {
  return await firestoreUpdateAppointment(id, updates);
};

// Delete appointment
export const deleteAppointment = async (id: string): Promise<void> => {
  return await firestoreDeleteAppointment(id);
};

// Update appointment status
export const updateAppointmentStatus = async (id: string, status: Appointment['status']): Promise<void> => {
  await updateAppointment(id, { status });
};